import { inject } from '@angular/core';
import { HttpEvent, HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { Observable } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { PageLoaderService } from '../services/page-loader.service';

/**
 * Drives PageLoaderService's ref count from every /api/ request. Registered first in
 * app.config.ts's withInterceptors array (before transientRetryInterceptor and authInterceptor)
 * so it wraps the whole request lifecycle, including retries and token acquisition — the loader
 * stays up for the full attempt instead of flickering between individual retries.
 */
export const pageLoaderInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
): Observable<HttpEvent<unknown>> => {
  const pageLoader = inject(PageLoaderService);

  if (req.headers.has('x-skip-page-loader')) {
    const passthroughRequest = req.clone({
      headers: req.headers.delete('x-skip-page-loader'),
    });
    return next(passthroughRequest);
  }

  if (!isApiRequest(req.url)) {
    return next(req);
  }

  pageLoader.beginHttpRequest();
  return next(req).pipe(
    finalize(() => {
      pageLoader.endHttpRequest();
    }),
  );
};

function isApiRequest(url: string): boolean {
  if (!url) {
    return false;
  }

  if (url.startsWith('/api/') || url.startsWith('api/')) {
    return true;
  }

  try {
    const parsed = new URL(url, window.location.origin);
    return parsed.pathname.startsWith('/api/');
  } catch {
    return false;
  }
}
